// Mi Anexo — resumen de solo lectura del Anexo en curso del cliente.
//
// El cliente ve lo que mandó y lo que el operador ya promovió a artículos.
// Nada acá se edita ni se borra: es una superficie de tranquilidad, no de
// trabajo. Los extracted_fields de las capturas NO se muestran — son estado
// interno de trabajo hasta que el operador los promueve.

import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, FileText, Image as ImageIcon, Mic } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { DESARROLLOS_TENANT_ID } from '../tenant';
import { useCliente } from './ClienteContext';
import SivarSendWordmark from '../../components/brand/SivarSendWordmark';
import ClienteActions from './ClienteActions';

const BUCKET_POR_TIPO = {
  image: 'desarrollos-images',
  factura: 'desarrollos-facturas',
  voice: 'desarrollos-voice',
};

// Helper local a propósito: el bundle del cliente no depende de módulos del
// operador, aunque allá exista uno equivalente.
const timeAgoEs = (iso) => {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'hace un momento';
  if (mins < 60) return `hace ${mins} min`;
  const horas = Math.floor(mins / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'hace 1 día' : `hace ${dias} días`;
};

const nombreArchivo = (ruta) => {
  const base = String(ruta || '').split('/').pop() || 'archivo';
  // Los nombres llevan un timestamp al frente: 1786505915131-Guia-004.jpeg
  return base.replace(/^\d+-/, '');
};

// raw_payload_url viene como "bucket/ruta/archivo".
const firmar = async (rawPath, sourceType) => {
  if (!rawPath) return null;
  let bucket = BUCKET_POR_TIPO[sourceType];
  let ruta = rawPath;
  const corte = rawPath.indexOf('/');
  if (corte > 0 && rawPath.slice(0, corte).startsWith('desarrollos-')) {
    bucket = rawPath.slice(0, corte);
    ruta = rawPath.slice(corte + 1);
  }
  if (!bucket) return null;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(ruta, 3600);
  if (error) {
    console.error('[Desarrollos/mi-anexo] no se pudo firmar', rawPath, error);
    return null;
  }
  return data?.signedUrl || null;
};

const Captura = ({ evento, urlFirmada }) => {
  const cuando = timeAgoEs(evento.created_at);

  if (evento.source_type === 'image') {
    return (
      <div className="flex items-center gap-3">
        {urlFirmada ? (
          <img
            src={urlFirmada}
            alt="Foto que enviaste"
            className="h-20 w-20 shrink-0 rounded-lg border border-slate-200 object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-[#f8fafc]">
            <ImageIcon size={18} className="text-slate-300" aria-hidden="true" />
          </div>
        )}
        <p className="text-xs text-slate-500">Foto — {cuando}</p>
      </div>
    );
  }

  if (evento.source_type === 'factura') {
    return (
      <div className="flex items-center gap-3">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-[#f8fafc]">
          <FileText size={18} className="text-slate-400" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-slate-700">
            {nombreArchivo(evento.raw_payload_url)}
          </p>
          <p className="mt-0.5 text-xs text-slate-500">Factura — {cuando}</p>
        </div>
      </div>
    );
  }

  if (evento.source_type === 'voice') {
    return (
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <Mic size={12} aria-hidden="true" />
          Nota de voz — {cuando}
        </p>
        {urlFirmada ? (
          <audio controls src={urlFirmada} className="w-full" preload="none">
            Tu navegador no puede reproducir esta nota de voz.
          </audio>
        ) : (
          <p className="text-xs text-slate-400">No pudimos cargar el audio.</p>
        )}
      </div>
    );
  }

  // manual
  const campos = Object.entries(evento.raw_manual_json || {});
  return (
    <div>
      <p className="mb-1.5 text-xs text-slate-500">Lo que escribiste — {cuando}</p>
      <div className="rounded-lg border border-slate-200 bg-[#f8fafc] p-2.5">
        {campos.length === 0 ? (
          <p className="text-xs text-slate-400">Sin datos.</p>
        ) : (
          campos.map(([k, v]) => (
            <p key={k} className="text-xs text-slate-600">
              <span className="font-semibold text-slate-500">{k}:</span>{' '}
              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </p>
          ))
        )}
      </div>
    </div>
  );
};

const MiAnexo = ({ onNavigate }) => {
  const { cliente } = useCliente();
  const [estado, setEstado] = useState('cargando'); // cargando | vacio | listo | error
  const [anexo, setAnexo] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [items, setItems] = useState([]);
  const [urls, setUrls] = useState({});

  const volver = useCallback(
    () => onNavigate('/desarrollos/cliente'),
    [onNavigate]
  );

  useEffect(() => {
    if (!cliente) {
      onNavigate('/desarrollos');
      return undefined;
    }

    let cancelado = false;
    (async () => {
      try {
        const { data: a, error: aError } = await supabase
          .from('desarrollos_anexos')
          .select('id, created_at, status')
          .eq('tenant_id', DESARROLLOS_TENANT_ID)
          .eq('client_id', cliente.id)
          .eq('status', 'live')
          .maybeSingle();
        if (aError) throw aError;
        if (cancelado) return;

        if (!a) {
          setEstado('vacio');
          return;
        }
        setAnexo(a);

        const [ev, it] = await Promise.all([
          supabase
            .from('desarrollos_intake_events')
            .select('id, source_type, raw_payload_url, raw_transcript, raw_manual_json, created_at')
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .eq('anexo_id', a.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('desarrollos_anexo_items')
            .select('id, descripcion, cantidad, unidad_medida, fob_unitario_usd, fob_total_usd, created_at')
            .eq('tenant_id', DESARROLLOS_TENANT_ID)
            .eq('anexo_id', a.id)
            .order('created_at', { ascending: true }),
        ]);
        if (ev.error) throw ev.error;
        if (it.error) throw it.error;
        if (cancelado) return;

        setEventos(ev.data || []);
        setItems(it.data || []);
        setEstado('listo');
      } catch (err) {
        console.error('[Desarrollos/mi-anexo] error cargando el Anexo', err);
        if (!cancelado) setEstado('error');
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [cliente, onNavigate]);

  // Firma perezosa de los adjuntos.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const pendientes = eventos.filter((e) => e.raw_payload_url && !urls[e.id]);
      if (pendientes.length === 0) return;
      const entradas = await Promise.all(
        pendientes.map(async (e) => [e.id, await firmar(e.raw_payload_url, e.source_type)])
      );
      if (cancelado) return;
      setUrls((prev) => ({ ...prev, ...Object.fromEntries(entradas) }));
    })();
    return () => {
      cancelado = true;
    };
  }, [eventos, urls]);

  if (!cliente) return null;

  const totalFob = items.reduce(
    (s, it) =>
      s +
      (Number(it.fob_total_usd) ||
        (Number(it.cantidad) || 0) * (Number(it.fob_unitario_usd) || 0)),
    0
  );

  const marco = (contenido) => (
    <div className="min-h-screen bg-[#f8fafc] p-5">
      <div className="mx-auto max-w-2xl pt-6">
        <div className="flex items-start justify-between gap-4">
          <SivarSendWordmark size="md" />
          <ClienteActions onNavigate={onNavigate} />
        </div>
        <button
          type="button"
          onClick={volver}
          className="mt-4 inline-flex min-h-[44px] items-center gap-1.5 text-xs font-bold text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft size={14} /> Volver
        </button>
        {contenido}
      </div>
    </div>
  );

  if (estado === 'cargando') {
    return marco(
      <div className="mt-5 space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl border border-slate-200 bg-white" />
        ))}
      </div>
    );
  }

  if (estado === 'error') {
    return marco(
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs text-red-600">
          No pudimos cargar tu Anexo. Revisá tu señal y volvé a intentar.
        </p>
      </div>
    );
  }

  if (estado === 'vacio') {
    return marco(
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-sm font-bold text-slate-900">No tenés un Anexo en curso</p>
        <p className="mt-2 text-xs leading-relaxed text-slate-500">
          Enviá una captura desde el inicio para empezar.
        </p>
        <button
          type="button"
          onClick={volver}
          className="mt-5 min-h-[44px] w-full rounded-lg bg-[var(--brand-teal)] px-5 text-sm font-semibold text-white transition hover:brightness-[1.08] sm:w-auto"
        >
          Volver al inicio
        </button>
      </div>
    );
  }

  return marco(
    <>
      {/* ── Estado ─────────────────────────────────────────────────────── */}
      <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-base font-bold text-slate-900">Tu Anexo en curso</h1>
            <p className="mt-0.5 text-xs text-slate-400">
              Creado {timeAgoEs(anexo.created_at)}
            </p>
          </div>
          <span className="shrink-0 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
            En preparación
          </span>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Estamos revisando tu información. Cuando esté listo, vas a poder descargar el Anexo
          desde acá.
        </p>
      </section>

      {/* ── Lo que enviaste ────────────────────────────────────────────── */}
      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-bold text-slate-900">Lo que enviaste</h2>
        {eventos.length === 0 ? (
          <p className="mt-3 text-xs leading-relaxed text-slate-400">
            Todavía no recibimos ninguna captura de tu parte.
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {eventos.map((evento) => (
              <div
                key={evento.id}
                className="border-b border-slate-100 pb-4 last:border-0 last:pb-0"
              >
                <Captura evento={evento} urlFirmada={urls[evento.id]} />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Artículos detectados ───────────────────────────────────────── */}
      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-bold text-slate-900">Artículos detectados</h2>
        {items.length === 0 ? (
          <p className="mt-3 text-xs leading-relaxed text-slate-400">
            Todavía no hemos procesado tus datos. Volvé en un rato.
          </p>
        ) : (
          <>
            <div className="mt-3 space-y-3">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">
                      {it.descripcion || 'Sin descripción'}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {Number(it.cantidad) || 0} × {it.unidad_medida || 'UNIDAD'}
                    </p>
                  </div>
                  <p className="shrink-0 font-mono text-sm text-slate-700">
                    $
                    {(
                      Number(it.fob_total_usd) ||
                      (Number(it.cantidad) || 0) * (Number(it.fob_unitario_usd) || 0)
                    ).toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 border-t border-slate-200 pt-3 text-right font-mono text-sm font-bold text-slate-900">
              Total FOB: ${totalFob.toFixed(2)}
            </p>
          </>
        )}
      </section>
    </>
  );
};

export default MiAnexo;
